import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaMicrophone,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaSpinner,
  FaSync,
  FaChartBar,
  FaListAlt,
  FaClock,
  FaEye, // ✅ ADD THIS IMPORT
} from "react-icons/fa";
import AdminBatchShadowingTaskSummary from "./AdminBatchShadowingTaskSummary";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ShadowingTask = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
  audioUrl: string;
  audioDownloadUrl: string;
  audioFilename: string;
  isActive: boolean;
};

type AssignedBatch = {
  id: string;
  taskId: number;
  taskNumber: number;
  taskLevel: string;
  batchId: number;
  batchName: string;
  assignedDate: string;
  deadline: string;
  status: string;
};

const AdminShadowingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ShadowingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showBatchResultsModal, setShowBatchResultsModal] = useState(false);

  // Assigned Batches state
  const [showAssignedBatchesModal, setShowAssignedBatchesModal] =
    useState(false);
  const [assignedBatches, setAssignedBatches] = useState<AssignedBatch[]>([]);
  const [assignedBatchesLoading, setAssignedBatchesLoading] = useState(false);
  const [activeAssignedTab, setActiveAssignedTab] = useState("A1");

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTasks(data);
        setLastUpdated(new Date());
      } else {
        setError("Failed to fetch shadowing tasks");
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedBatches = async () => {
    setAssignedBatchesLoading(true);
    try {
      const tasksRes = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks`,
        {
          credentials: "include",
        }
      );
      if (!tasksRes.ok) throw new Error("Failed to fetch tasks");
      const tasks = await tasksRes.json();

      const batchMap = new Map();
      try {
        const batchesRes = await fetch(
          `${API_BASE_URL}/api/admin/shadowing-tasks/active-batches`,
          { credentials: "include" }
        );
        if (batchesRes.ok) {
          const batches = await batchesRes.json();
          batches.forEach((batch: any) => {
            batchMap.set(batch.id, batch.name);
          });
        }
      } catch (err) {
        console.log("Could not fetch batch names, will use IDs");
      }

      const allAssignments: AssignedBatch[] = [];

      for (const task of tasks) {
        try {
          const assignRes = await fetch(
            `${API_BASE_URL}/api/admin/shadowing-tasks/${task.id}/assigned-batches`,
            { credentials: "include" }
          );

          if (assignRes.ok) {
            const assignments = await assignRes.json();

            assignments.forEach((assignment: any) => {
              const deadline = new Date(assignment.deadline);
              const now = new Date();

              let status = "active";
              if (deadline < now) {
                status = "completed";
              } else if (
                deadline > new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
              ) {
                status = "upcoming";
              }

              allAssignments.push({
                id: `${task.id}-${
                  assignment.batchId
                }-${Date.now()}-${Math.random()}`,
                taskId: task.id,
                taskNumber: task.taskNumber,
                taskLevel: task.level || "Unspecified",
                batchId: assignment.batchId,
                batchName:
                  assignment.batchName ||
                  batchMap.get(assignment.batchId) ||
                  `Batch ${assignment.batchId}`,
                assignedDate:
                  assignment.assignedDate || new Date().toISOString(),
                deadline: assignment.deadline,
                status: status,
              });
            });
          }
        } catch (err) {
          console.error(`Error fetching assignments for task ${task.id}:`, err);
        }
      }

      console.log("📊 Loaded assignments:", allAssignments);
      setAssignedBatches(allAssignments);
    } catch (err) {
      console.error("Failed to load assigned batches:", err);
      setAssignedBatches([]);
    } finally {
      setAssignedBatchesLoading(false);
    }
  };

  const handleViewAssignedBatches = () => {
    setShowAssignedBatchesModal(true);
    fetchAssignedBatches();
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchTasks();
    setRefreshing(false);
  };

  const handleDelete = async (taskId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this shadowing task? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setTasks(tasks.filter((task) => task.id !== taskId));
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete task");
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      alert("Network error. Please try again.");
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return "No description";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Group tasks by level
  const tasksByLevel = tasks.reduce<Record<string, ShadowingTask[]>>(
    (acc, task) => {
      const level = task.level || "Unspecified";
      if (!acc[level]) acc[level] = [];
      acc[level].push(task);
      return acc;
    },
    {}
  );

  const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "Unspecified"];
  const sortedLevels = Object.keys(tasksByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // Group assigned batches by level
  const assignedBatchesByLevel = assignedBatches.reduce<
    Record<string, AssignedBatch[]>
  >((acc, assignment) => {
    const level = assignment.taskLevel || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(assignment);
    return acc;
  }, {});

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; icon: JSX.Element; className: string }
    > = {
      active: {
        label: "Active",
        icon: <FaClock />,
        className: "bg-green-100 text-green-800",
      },
      upcoming: {
        label: "Upcoming",
        icon: <FaClock />,
        className: "bg-yellow-100 text-yellow-800",
      },
      completed: {
        label: "Completed",
        icon: <FaClock />,
        className: "bg-blue-100 text-blue-800",
      },
      expired: {
        label: "Expired",
        icon: <FaClock />,
        className: "bg-red-100 text-red-800",
      },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  const renderAssignedBatchesTable = (level: string) => {
    const assignments = assignedBatchesByLevel[level] || [];

    if (assignments.length === 0) {
      return (
        <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow">
          <FaMicrophone className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-semibold">No assignments for this level yet.</p>
        </div>
      );
    }

    const batchAssignmentDates = new Map();
    assignments.forEach((a) => {
      const currentDate = batchAssignmentDates.get(a.batchId);
      if (!currentDate || new Date(a.assignedDate) > new Date(currentDate)) {
        batchAssignmentDates.set(a.batchId, a.assignedDate);
      }
    });

    const uniqueBatches = Array.from(
      new Map(
        assignments.map((a) => [
          a.batchId,
          { id: a.batchId, name: a.batchName },
        ])
      ).values()
    ).sort((a, b) => {
      const dateA = new Date(batchAssignmentDates.get(a.id) || 0);
      const dateB = new Date(batchAssignmentDates.get(b.id) || 0);
      return dateB.getTime() - dateA.getTime();
    });

    const taskColumns = Array.from({ length: 14 }, (_, i) => i + 1);

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-30">
              <tr className="bg-gradient-to-r from-[#009fe3] to-cyan-500">
                <th className="sticky left-0 z-40 bg-[#009fe3] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-white border-r-2 border-white">
                  Batch Name
                </th>
                {taskColumns.map((taskNum) => (
                  <th
                    key={taskNum}
                    className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white border-r border-cyan-400 min-w-[100px]"
                  >
                    <span className="font-bold text-base">Task {taskNum}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {uniqueBatches.map((batch, batchIdx) => (
                <tr
                  key={batch.id}
                  className={`${
                    batchIdx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-blue-50 transition-colors`}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-gray-900 border-r-2 border-gray-200">
                    {batch.name}
                  </td>
                  {taskColumns.map((taskNum) => {
                    const batchAssignments = assignments
                      .filter((a) => a.batchId === batch.id)
                      .sort((x, y) => x.taskNumber - y.taskNumber);

                    const actualAssignment = batchAssignments[taskNum - 1];

                    return (
                      <td
                        key={`${batch.id}-task${taskNum}`}
                        className="px-3 py-3 text-center border-r border-gray-200"
                      >
                        {actualAssignment ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(
                                actualAssignment.deadline
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                              })}
                            </div>
                            <div className="mt-1">
                              {getStatusBadge(actualAssignment.status)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xl">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTaskTable = (level: string, taskList: ShadowingTask[]) => {
    const sortedTasks = [...taskList].sort(
      (a, b) => a.taskNumber - b.taskNumber
    );

    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaMicrophone />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {sortedTasks.length} / 14 tasks
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Task #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Audio
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sortedTasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No shadowing tasks found for this level.
                    </td>
                  </tr>
                ) : (
                  sortedTasks.map((task, index) => (
                    <tr
                      key={task.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                          {task.taskNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {task.level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700 text-sm">
                          {truncateText(task.taskDescription, 120)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {task.audioDownloadUrl ? (
                            <audio
                              controls
                              className="w-48"
                              src={task.audioDownloadUrl}
                            >
                              Your browser does not support audio playback.
                            </audio>
                          ) : (
                            <span className="text-gray-400 text-sm">
                              No audio
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {/* ✅ UPDATED ACTIONS SECTION WITH 4 BUTTONS */}
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/shadowing-tasks/${task.id}/assign`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="Assign to Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>

                          {/* ✅ NEW: VIEW SUBMISSIONS BUTTON */}
                          <button
                            onClick={() =>
                              navigate(
                                `/admin/shadowing-tasks/${task.id}/submissions`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-semibold"
                            title="View Submissions"
                          >
                            <FaEye size={14} />
                            Submissions
                          </button>

                          <button
                            onClick={() =>
                              navigate(`/admin/shadowing-tasks/${task.id}/edit`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            title="Edit Task"
                          >
                            <FaEdit size={14} />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(task.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-semibold"
                            title="Delete Task"
                          >
                            <FaTrash size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaMicrophone />}
        title="Manage Shadowing Tasks"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Shadowing Tasks" },
        ]}
      />

      <div className="flex items-center justify-end gap-3 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <span className="text-gray-500 text-xs sm:text-sm" style={{ fontSize: "16px" }}>
          {lastUpdated.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base backdrop-blur-sm"
          style={{ fontSize: "16px" }}
        >
          <FaSync
            className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 bg-white rounded-lg shadow-lg p-4">
          <button
            onClick={() => navigate("/admin/shadowing-tasks/create")}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-[#009fe3] hover:bg-cyan-600 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaPlus />
            Create Shadowing Task
          </button>
          <button
            onClick={handleViewAssignedBatches}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-purple-600 hover:bg-purple-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaListAlt />
            View Assigned Batches
          </button>
          <button
            onClick={() => setShowBatchResultsModal(true)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaChartBar />
            Batch Results
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading shadowing tasks...
          </div>
        ) : (
          <>
            {sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaMicrophone className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  No Shadowing Tasks Yet
                </h3>
                <p className="mb-4">
                  Create your first shadowing task to get started!
                </p>
                <button
                  onClick={() => navigate("/admin/shadowing-tasks/create")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors font-semibold"
                >
                  <FaPlus />
                  Create Shadowing Task
                </button>
              </div>
            ) : (
              sortedLevels.map((level) =>
                renderTaskTable(level, tasksByLevel[level])
              )
            )}
          </>
        )}
      </main>

      {/* Assigned Batches Modal */}
      {showAssignedBatchesModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowAssignedBatchesModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAssignedBatchesModal(false)}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 text-3xl font-bold bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-all"
              aria-label="Close"
            >
              &times;
            </button>

            <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-white z-50">
              <h3 className="text-2xl font-bold text-[#009fe3] flex items-center gap-2">
                <FaListAlt />
                Assigned Batches & Deadlines
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {assignedBatchesLoading ? (
                <div className="text-center text-[#009fe3] flex items-center justify-center gap-2 py-10">
                  <FaSpinner className="animate-spin" />
                  Loading assigned batches...
                </div>
              ) : (
                <>
                  <div className="sticky top-0 bg-white z-40 pb-4 mb-6 border-b border-gray-200">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {levelOrder.slice(0, 6).map((level) => {
                        const count =
                          assignedBatchesByLevel[level]?.length || 0;
                        return (
                          <button
                            key={level}
                            onClick={() => setActiveAssignedTab(level)}
                            className={`px-4 py-2 rounded-t-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                              activeAssignedTab === level
                                ? "bg-[#009fe3] text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            Level {level}
                            {count > 0 && (
                              <span
                                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  activeAssignedTab === level
                                    ? "bg-white/20"
                                    : "bg-gray-300"
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {renderAssignedBatchesTable(activeAssignedTab)}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Results Modal */}
      {showBatchResultsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowBatchResultsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBatchResultsModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3] flex items-center gap-2">
              <FaChartBar />
              Batch Shadowing Task Results Summary
            </h3>
            <AdminBatchShadowingTaskSummary />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShadowingTasksPage;
